import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  KEEP_EDITING,
  SHRINK_ACCEPTED_SAVE,
  SHRINK_ACCEPTED_SCENE,
} from '../../utils/writerCopy';
import './ShrinkGuardDialog.css';

/**
 * Ask before a hand edit replaces most of an accepted scene.
 * Keep editing leaves the typed words in the box and does not save.
 * The same shell is used for other scene confirms (restore).
 */
export default function ShrinkGuardDialog({
  title = SHRINK_ACCEPTED_SCENE,
  keepLabel = KEEP_EDITING,
  confirmLabel = SHRINK_ACCEPTED_SAVE,
  titleId = 'sw-shrink-guard-title',
  onSave,
  onKeepEditing,
}) {
  const keepRef = useRef(null);
  const saveRef = useRef(null);

  useEffect(() => {
    keepRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onKeepEditing?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onKeepEditing]);

  const onKeyDown = (event) => {
    if (event.key !== 'Tab') return;
    const first = keepRef.current;
    const last = saveRef.current;
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const handleBackdrop = (event) => {
    if (event.target === event.currentTarget) onKeepEditing?.();
  };

  return createPortal(
    <div className="sw-shrink-guard-backdrop" onClick={handleBackdrop}>
      <div
        className="sw-shrink-guard"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={onKeyDown}
      >
        <h3 id={titleId} className="sw-shrink-guard__title">
          {title}
        </h3>
        <div className="sw-shrink-guard__actions">
          <button
            type="button"
            ref={keepRef}
            className="sw-shrink-guard__btn sw-shrink-guard__btn--keep"
            onClick={onKeepEditing}
          >
            {keepLabel}
          </button>
          <button
            type="button"
            ref={saveRef}
            className="sw-shrink-guard__btn sw-shrink-guard__btn--save"
            onClick={onSave}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
